import { useEffect, useState } from 'react';
import type {
  AggregatedStats,
  PixelStreaming,
  StatsReceivedEvent,
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';

export interface IStreamStats {
  fps: number | null;
  bitrateKbps: number | null;
  width: number | null;
  height: number | null;
  packetsLost: number | null;
  jitterMs: number | null;
  freezeCount: number | null;
  totalFreezeDurationMs: number | null;
  qp: number | null;
  rttMs: number | null;
  codec: string | null;
  turnRelayed: boolean;
}

const EMPTY_STATS: IStreamStats = {
  fps: null,
  bitrateKbps: null,
  width: null,
  height: null,
  packetsLost: null,
  jitterMs: null,
  freezeCount: null,
  totalFreezeDurationMs: null,
  qp: null,
  rttMs: null,
  codec: null,
  turnRelayed: false,
};

const toStats = (aggregated: AggregatedStats): Omit<IStreamStats, 'turnRelayed'> => {
  const video = aggregated.inboundVideoStats;
  const activePair = aggregated.getActiveCandidatePair();
  const codec = video?.codecId ? aggregated.codecs.get(video.codecId) : undefined;

  return {
    fps: video?.framesPerSecond ?? null,
    bitrateKbps:
      video?.bitrate != null ? Math.round(video.bitrate / 1000) : null,
    width: video?.frameWidth ?? null,
    height: video?.frameHeight ?? null,
    packetsLost: video?.packetsLost ?? null,
    jitterMs: video?.jitter != null ? Math.round(video.jitter * 1000) : null,
    freezeCount: video?.freezeCount ?? null,
    totalFreezeDurationMs:
      video?.totalFreezesDuration != null
        ? Math.round(video.totalFreezesDuration * 1000)
        : null,
    qp: aggregated.sessionStats?.videoEncoderAvgQP ?? null,
    rttMs:
      activePair?.currentRoundTripTime != null
        ? Math.round(activePair.currentRoundTripTime * 1000)
        : null,
    codec: codec?.mimeType?.replace('video/', '') ?? null,
  };
};

export const useStreamStats = (
  pixelStreaming: PixelStreaming | null,
): IStreamStats => {
  const [stats, setStats] = useState<IStreamStats>(EMPTY_STATS);

  useEffect(() => {
    setStats(EMPTY_STATS);

    if (!pixelStreaming) {
      return;
    }

    const handleStats = (event: Event & StatsReceivedEvent) => {
      setStats((prev) => ({
        ...toStats(event.data.aggregatedStats),
        turnRelayed: prev.turnRelayed,
      }));
    };

    const handleTurnRelay = () => {
      setStats((prev) => ({ ...prev, turnRelayed: true }));
    };

    pixelStreaming.addEventListener('statsReceived', handleStats);
    pixelStreaming.addEventListener('webRtcTCPRelayDetected', handleTurnRelay);

    return () => {
      pixelStreaming.removeEventListener('statsReceived', handleStats);
      pixelStreaming.removeEventListener(
        'webRtcTCPRelayDetected',
        handleTurnRelay,
      );
    };
  }, [pixelStreaming]);

  return stats;
};
